import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../../ui/Icon";
import { t } from "../../../lib/i18n";
import { sainsClip, spoken } from "@jomcerdik/shared";
import { useSains } from "./sainsLang";
import { canSpeak } from "../../../lib/speak";
import { loadManifest, playQueue, prefetchClips } from "../../../lib/bacaAudio";
import { Card } from "../kit";
import { Result } from "./common";

/* Pieces shared by the 2D and 3D versions of the Sains games. */

export const NEXT = "rounded-full bg-violet-500 px-5 py-2.5 text-sm font-extrabold text-white shadow-md active:scale-95";
export const GHOST = "rounded-full bg-white/90 px-4 py-2.5 text-sm font-bold text-slate-700 shadow ring-1 ring-black/10 active:scale-95";
export function webglOk(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}
export const reduced = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** The clips for a script: the first line is a heading (word), the rest are sentences. Same texts the audio pipeline generates. */
const scriptClips = (lines: readonly string[], lang: "ms" | "en") => lines.map((l, i) => sainsClip(i === 0 && lines.length > 1 ? "word" : "line", l, lang));

/** Reads a script aloud with the generated voice (Malay or English); the device voice is only the fallback when a clip is missing. */
export function ListenButton({ lines, dark }: { lines: readonly string[]; dark?: boolean }) {
  const { lang } = useSains();
  const key = lines.join("\n") + lang;
  const clips = useMemo(() => { try { return scriptClips(lines, lang); } catch { return []; } }, [key]);
  useEffect(() => { void loadManifest().then(() => prefetchClips(clips)).catch(() => undefined); }, [clips]);
  if (!canSpeak() && clips.length === 0) return null;
  return (
    <button type="button" onClick={() => playQueue(clips, lines.map((l) => spoken(l, lang)).join(" "))} aria-label={t("Dengar")}
      className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${dark ? "bg-white/15 text-white" : "bg-emerald-500 text-white"}`}>
      <Icon name="speaker" className="h-4 w-4" />
    </button>
  );
}

export type QuizItem = { prompt: string; options: string[]; answer: string };
export const QUIZ_ROUNDS = 6;

export function MiniQuiz({ items, details, onExit }: { items: QuizItem[]; details: Record<string, string>; onExit: () => void }) {
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [seed, setSeed] = useState(0);
  const stable = useMemo(() => details, [details]);
  if (i >= items.length) {
    return <Result key={seed} game="baca" score={score} max={items.length} details={stable} onAgain={() => { setI(0); setScore(0); setPicked(null); setSeed((s) => s + 1); onExit(); }} />;
  }
  const q = items[i]!;
  const pick = (o: string) => {
    if (picked !== null) return;
    setPicked(o);
    if (o === q.answer) setScore((s) => s + 1);
  };
  return (
    <Card className="animate-rise space-y-4">
      <div className="flex items-center justify-between text-xs font-bold text-slate-400">
        <span>{i + 1}/{items.length}</span>
        <span className="inline-flex items-center gap-1"><Icon name="star" className="h-4 w-4 text-amber-400" />{score}</span>
      </div>
      <div className="flex items-start gap-3">
        <p className="flex-1 text-xl font-extrabold leading-snug text-slate-900">{q.prompt}</p>
        <ListenButton lines={[q.prompt]} />
      </div>
      <div className="grid gap-2">
        {q.options.map((o) => {
          const state = picked === null ? "" : o === q.answer ? "bg-emerald-500 text-white ring-emerald-600" : o === picked ? "bg-rose-100 text-rose-700 ring-rose-300" : "opacity-60";
          return (
            <button key={o} type="button" onClick={() => pick(o)} disabled={picked !== null}
              className={`rounded-2xl bg-card px-4 py-3 text-left text-base font-bold text-slate-800 ring-1 ring-black/10 transition active:scale-[0.98] ${state}`}>
              {o}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <div className="space-y-2 animate-rise">
          <p className={`text-center text-base font-extrabold ${picked === q.answer ? "text-emerald-600" : "text-rose-600"}`}>
            {picked === q.answer ? t("Betul! Pandai!") : `${t("Jawapan: ")}${q.answer}`}
          </p>
          <button type="button" onClick={() => { setI((n) => n + 1); setPicked(null); }} className={`${NEXT} w-full py-3 text-base`}>{t("Seterusnya")}</button>
        </div>
      )}
    </Card>
  );
}

